'use client';

import { getClientApplicationContext } from '@hc-frontend/core-context';
import { CountMetricName, type Tags } from '@hc-frontend/core-types';
import { useJourney } from '@hc-frontend/core-ui';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadScriptWithRetry } from '../../../utils/load-script-with-retry';
import { ConsentScriptsContext } from './consent-scripts-context';
import type {
  ConsentScriptsContextValue,
  ConsentScriptsProviderProps,
  ConsentScriptsState,
} from './consent-scripts-provider.types';
import {
  buildJornayaSrc,
  buildTrustedFormSrc,
  isJornayaToken,
  isTrustedFormCertUrl,
  JORNAYA_SCRIPT_ID,
  JORNAYA_TOKEN_WAIT_MS,
  TRUSTED_FORM_FIELD,
  TRUSTED_FORM_POLL_MS,
  TRUSTED_FORM_SCRIPT_ID,
} from './constants';

declare global {
  interface Window {
    LeadiD?: { token: string; reInit?: () => void };
    onGetLeadId: (universalId: string) => void;
    trustedFormCertUrlCallback: (certificateId: string) => void;
    /** Defined by the TrustedForm script once it has run. */
    trustedForm?: unknown;
  }
}

const INITIAL_STATE: ConsentScriptsState = {
  jornaya: { status: 'loading', attempts: 0 },
  trustedForm: { status: 'loading', attempts: 0 },
};

/** Last path segment, e.g. `zip-code` for `/medicare/survey/zip-code/`. */
const plateNameFromPath = (pathname: string | null): string =>
  pathname?.split('/').filter(Boolean).pop() ?? '';

/** First valid TrustedForm certificate URL in the document, if any. */
const readCertFromDom = (): string | undefined =>
  Array.from(
    document.querySelectorAll<HTMLInputElement>(
      `input[name="${TRUSTED_FORM_FIELD}"]`,
    ),
  ).find((input) => isTrustedFormCertUrl(input.value))?.value;

/**
 * Loads the Jornaya and TrustedForm consent scripts from the survey layout, so
 * they start on the first plate instead of the PII plate, and retries them
 * when they fail (MP-3950).
 *
 * - Owns `window.onGetLeadId` and `window.trustedFormCertUrlCallback` while
 *   mounted and restores the previous values on unmount, so TYP components
 *   keep working after the user leaves the survey.
 * - Keeps the first Jornaya token for the whole survey. A later token, including
 *   one minted by `reInit` on a plate change, is ignored.
 * - A script that failed to load (or loaded without initialising) is
 *   re-injected with backoff. A Jornaya script that initialised but produced no
 *   token gets one `LeadiD.reInit()`; it is never reloaded.
 * - Adopts scripts already present in the document (e.g. after a cross-funnel
 *   client navigation) instead of loading them twice.
 * - Emits `consent-script-load` per attempt and `jornaya-result` with
 *   `stage=early`. Token and certificate values are never put in metrics.
 *
 * PII plates read the state through `usePiiConsentScripts`.
 */
export function ConsentScriptsProvider({
  product,
  placement,
  children,
}: ConsentScriptsProviderProps) {
  const [state, setState] = useState<ConsentScriptsState>(INITIAL_STATE);
  const stateRef = useRef(state);
  stateRef.current = state;

  const { journeyId } = useJourney();
  const pathname = usePathname();
  // Metrics fire from long-lived async flows; read tags from refs so they
  // describe the plate the user is on when the event happens.
  const tagsRef = useRef<Tags>({});
  tagsRef.current = {
    product,
    placement,
    journey_id: journeyId,
    plate_name: plateNameFromPath(pathname),
  };

  const track = useCallback((name: CountMetricName, tags: Tags) => {
    getClientApplicationContext(ConsentScriptsProvider).trackCountMetric({
      name,
      tags: { ...tagsRef.current, ...tags },
    });
  }, []);

  const piiMountCountRef = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let certPoll: ReturnType<typeof setInterval> | undefined;
    let storedToken: string | undefined;

    const setJornaya = (
      update: (
        jornaya: ConsentScriptsState['jornaya'],
      ) => ConsentScriptsState['jornaya'],
    ) =>
      setState((current) => ({ ...current, jornaya: update(current.jornaya) }));
    const setTrustedForm = (
      update: (
        trustedForm: ConsentScriptsState['trustedForm'],
      ) => ConsentScriptsState['trustedForm'],
    ) =>
      setState((current) => ({
        ...current,
        trustedForm: update(current.trustedForm),
      }));

    const jornayaResult = (result: 'success' | 'error' | 'no-token') =>
      track(CountMetricName.Enum['jornaya-result'], {
        jornaya_result: result,
        stage: 'early',
      });

    const receiveToken = (token: string) => {
      if (!isJornayaToken(token) || storedToken || signal.aborted) return;
      storedToken = token;
      jornayaResult('success');
      setJornaya((jornaya) => ({ ...jornaya, status: 'token', token }));
    };

    const receiveCert = (certUrl: string) => {
      if (!isTrustedFormCertUrl(certUrl) || signal.aborted) return;
      clearInterval(certPoll);
      setTrustedForm((trustedForm) => ({
        ...trustedForm,
        status: 'cert',
        certUrl,
      }));
    };

    const previousOnGetLeadId = window.onGetLeadId;
    const previousCertCallback = window.trustedFormCertUrlCallback;
    window.onGetLeadId = receiveToken;
    window.trustedFormCertUrlCallback = receiveCert;

    /** No token yet: `reInit` once, wait again, then settle on `no-token`. */
    const awaitToken = () => {
      timers.push(
        setTimeout(() => {
          if (storedToken) return;
          window.LeadiD?.reInit?.();
          timers.push(
            setTimeout(() => {
              if (storedToken) return;
              const token = window.LeadiD?.token;
              if (isJornayaToken(token)) {
                receiveToken(token);
                return;
              }
              track(CountMetricName.Enum['consent-script-load'], {
                provider: 'jornaya',
                result: 'no-token',
                stage: 'early',
              });
              jornayaResult('no-token');
              setJornaya((jornaya) => ({ ...jornaya, status: 'no-token' }));
            }, JORNAYA_TOKEN_WAIT_MS),
          );
        }, JORNAYA_TOKEN_WAIT_MS),
      );
    };

    const startJornaya = async () => {
      if (window.LeadiD) {
        track(CountMetricName.Enum['consent-script-load'], {
          provider: 'jornaya',
          attempt: 0,
          result: 'adopted',
          stage: 'early',
        });
        setJornaya((jornaya) => ({ ...jornaya, status: 'loaded' }));
        if (isJornayaToken(window.LeadiD.token)) {
          receiveToken(window.LeadiD.token);
        } else {
          awaitToken();
        }
        return;
      }

      const { loaded, attempts } = await loadScriptWithRetry({
        id: JORNAYA_SCRIPT_ID,
        buildSrc: buildJornayaSrc,
        isReady: () => Boolean(window.LeadiD),
        signal,
        onAttempt: ({ attempt, result, durationMs }) =>
          track(CountMetricName.Enum['consent-script-load'], {
            provider: 'jornaya',
            attempt,
            result,
            duration_ms: durationMs,
            stage: 'early',
          }),
      });
      if (signal.aborted) return;

      if (!loaded) {
        jornayaResult('error');
        setJornaya((jornaya) => ({ ...jornaya, status: 'failed', attempts }));
        return;
      }

      setJornaya((jornaya) => ({
        ...jornaya,
        attempts,
        status: jornaya.status === 'token' ? 'token' : 'loaded',
      }));
      awaitToken();
    };

    const startTrustedForm = async () => {
      certPoll = setInterval(() => {
        const certUrl = readCertFromDom();
        if (certUrl) receiveCert(certUrl);
      }, TRUSTED_FORM_POLL_MS);

      // TrustedForm removes its own <script> once it runs, so its global (not
      // the element) tells whether it is already on the page.
      if (window.trustedForm) {
        track(CountMetricName.Enum['consent-script-load'], {
          provider: 'trustedform',
          attempt: 0,
          result: 'adopted',
          stage: 'early',
        });
        setTrustedForm((trustedForm) => ({ ...trustedForm, status: 'loaded' }));
        return;
      }

      const { loaded, attempts } = await loadScriptWithRetry({
        id: TRUSTED_FORM_SCRIPT_ID,
        buildSrc: buildTrustedFormSrc,
        signal,
        onAttempt: ({ attempt, result, durationMs }) =>
          track(CountMetricName.Enum['consent-script-load'], {
            provider: 'trustedform',
            attempt,
            result,
            duration_ms: durationMs,
            stage: 'early',
          }),
      });
      if (signal.aborted) return;

      if (!loaded) clearInterval(certPoll);
      setTrustedForm((trustedForm) => ({
        ...trustedForm,
        attempts,
        status:
          trustedForm.status === 'cert' ? 'cert' : loaded ? 'loaded' : 'failed',
      }));
    };

    startJornaya();
    startTrustedForm();

    return () => {
      controller.abort();
      timers.forEach(clearTimeout);
      clearInterval(certPoll);
      if (window.onGetLeadId === receiveToken) {
        window.onGetLeadId = previousOnGetLeadId;
      }
      if (window.trustedFormCertUrlCallback === receiveCert) {
        window.trustedFormCertUrlCallback = previousCertCallback;
      }
    };
  }, [track]);

  const notifyPiiMounted = useCallback(() => {
    const { jornaya, trustedForm } = stateRef.current;
    track(CountMetricName.Enum['consent-tokens-at-pii'], {
      jornaya_available: Boolean(jornaya.token),
      trustedform_available: Boolean(trustedForm.certUrl),
      jornaya_status: jornaya.status,
      trustedform_status: trustedForm.status,
      jornaya_attempts: jornaya.attempts,
      trustedform_attempts: trustedForm.attempts,
    });

    piiMountCountRef.current += 1;
    const id = piiMountCountRef.current;
    // The script stays the one loaded on the first plate. Calling `reInit`
    // here would mint a second token for the same survey.
    setState((current) => ({
      ...current,
      piiMount: { id, refreshed: true },
    }));
    return id;
  }, [track]);

  const value = useMemo<ConsentScriptsContextValue>(
    () => ({ ...state, notifyPiiMounted }),
    [state, notifyPiiMounted],
  );

  return (
    <ConsentScriptsContext.Provider value={value}>
      {children}
    </ConsentScriptsContext.Provider>
  );
}
